// Is an element currently inside the viewport?
//
// Used by the journey shell to decide whether the forward action at the end of
// a step is on screen. While it is, the ribbon stays quiet; once the customer
// has scrolled it out of view — or hasn't reached it yet on a long form — the
// ribbon shows a compact one, so a forward action is always reachable without
// ever pinning a bar to the bottom of the window.
//
// Environments without IntersectionObserver (jsdom, very old WebViews) report
// the element as visible, which degrades to "the footer is the only action".
import { useEffect, useState } from "react";

/**
 * @param {React.RefObject<HTMLElement>} ref
 * @param {{ rootMargin?: string, threshold?: number }} [options]
 * @returns {boolean}
 */
export const useInView = (ref, { rootMargin = "0px", threshold = 0.2 } = {}) => {
  const [inView, setInView] = useState(true);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin, threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, rootMargin, threshold]);

  return inView;
};

export default useInView;
